import { useQuery } from '@tanstack/react-query';

import { fetchPremiumBody } from '@api-service/content';
import type { PremiumBodyBlock } from '@api-service/content';
import { useAccessToken } from '@hooks/useAccessToken';
import { useAuth0User } from '@hooks/useAuth0User';
import { hydrateArticleBlocks } from '@lib/article-blocks/hydrate';
import type { ArticleBlockData } from '@lib/article-blocks/types';
import { buildContentDocumentSelector } from '@lib/sanity';
import type { SanityArticlePage } from '@lib/sanity';
import { hasSufficientPrivileges, ROLE_LEVELS } from '@lib/utils';

const PREMIUM_ACCESS_LEVEL = 'premium';
// Matches the API's own cache and the page's ISR window, so a member who
// navigates back to an article within it does not refetch a body that cannot
// have changed from their point of view.
const PREMIUM_BODY_STALE_TIME_IN_MS = 10 * 60 * 1000;

export type PremiumBodyState =
  | { status: 'not-applicable' }
  | { status: 'loading' }
  | { status: 'locked' }
  | { status: 'error'; retry: () => void }
  | { status: 'ready'; body: PremiumBodyBlock[]; blockData: ArticleBlockData };

type PremiumBodyResult = {
  body?: PremiumBodyBlock[];
  blockData?: ArticleBlockData;
};

/**
 * Resolves the gated half of a premium article or team reveal for the current
 * visitor. The static page only ever holds the free body, so an entitled
 * member fetches the rest from the content API with their token and the
 * blocks inside it are hydrated in the browser. Everyone else is `locked` and
 * sees the paywall. The client-side entitlement check only decides whether to
 * attempt the fetch; the API is the real gate.
 */
export const usePremiumBody = (
  page: SanityArticlePage | undefined
): PremiumBodyState => {
  const { user, isLoading: isUserLoading } = useAuth0User();
  const { accessToken } = useAccessToken();

  const isPremium = page?.accessLevel === PREMIUM_ACCESS_LEVEL;
  const isEntitled = hasSufficientPrivileges(user, ROLE_LEVELS.PRO);
  const selector = page ? buildContentDocumentSelector(page) : undefined;
  const isFetchEnabled =
    isPremium && isEntitled && Boolean(accessToken) && Boolean(selector);

  const { data, isError, isSuccess, refetch } = useQuery<PremiumBodyResult>({
    queryKey: [
      'premium-body',
      selector?.type,
      selector?.type === 'teamReveal' ? selector.authorSlug : undefined,
      selector?.slug,
      user?.sub
    ],
    queryFn: async () => {
      if (!selector || !accessToken) return {};
      const body = await fetchPremiumBody(selector, accessToken);
      if (!body) return {};
      return { body, blockData: await hydrateArticleBlocks(body) };
    },
    enabled: isFetchEnabled,
    staleTime: PREMIUM_BODY_STALE_TIME_IN_MS,
    refetchOnWindowFocus: false,
    retry: false
  });

  if (!isPremium) return { status: 'not-applicable' };
  if (isUserLoading) return { status: 'loading' };
  if (!isEntitled) return { status: 'locked' };
  if (isError) return { status: 'error', retry: () => void refetch() };
  if (isSuccess) {
    return data.body && data.blockData
      ? { status: 'ready', body: data.body, blockData: data.blockData }
      : { status: 'locked' };
  }
  return { status: 'loading' };
};
